import {
  MODEL_TYPE,
  MODEL_TYPE_KEY_BY_VALUE,
  MODEL_PHASE_TYPE,
  DOCUMENT_MAIN_TYPE,
  DOCUMENT_LINE_TYPE, MODEL_TYPE_MAPPER, DOCUMENT_MAIN_TYPE_MAPPER, SETTING_PURCHASE_PRICE_SOURCE_VALUES
} from '../constants'
import axios from 'axios'
import enumConstants from '../enumConstants.json'
import links from '../links.json'
import { toast } from 'sonner'
import { t } from 'i18next'
import { route } from './route.jsx'
import NewDocumentHeaders from '../components/Shared/Document/NewDocumentHeaders.jsx'
import React from 'react'

const lineAllColumns = [
  'type',
  'model_id',
  'description',
  'description_2',

  'product_id',
  'product_type',

  'package_uom_id',
  'package_quantity',

  'uom_id',
  'quantity',

  'quantity_to_return',
  'quantity_returned',

  'quantity_released',

  'quantity_to_order',
  'quantity_ordered',
  'outstanding_quantity_to_order',

  'quantity_to_ship',
  'quantity_shipped',
  'outstanding_quantity_to_ship',

  'quantity_to_invoice',
  'quantity_invoiced',
  'outstanding_quantity_to_invoice',

  'list_price',
  'list_price_exc_vat',

  'price',
  'price_exc_vat',

  'discount_percentage',
  'unit_discount_exc_vat',
  'unit_discount',
  'discount_amount',
  'discount_total',

  'base_amount',
  'amount',
  'vat_product_group_id',
  'vat_percent',
  'vat_amount',
  'base_total',
  'total',

  'note',

  'vendor_id'
]

const lineReturnBlockColumns = lineAllColumns.filter(f => f !== 'quantity_to_return')

const lineAdditionalQuantityColumns = [
  'quantity_to_order',
  'quantity_to_ship',
  'quantity_to_invoice'
]

const headerInClosedPhases = (documentHeader) => [MODEL_PHASE_TYPE.CLOSED, MODEL_PHASE_TYPE.POSTED, MODEL_PHASE_TYPE.FULLY_PROCESSED].includes(documentHeader.status_phase_type)

const DOCUMENT_LINE_HELPER_FUNCTION = {
  lineHasShipping: (type) => {
    return [
      MODEL_TYPE.SALES_ORDER,
      MODEL_TYPE.SALES_RETURN_ORDER,
      MODEL_TYPE.PURCHASE_ORDER,
      MODEL_TYPE.PURCHASE_RETURN_ORDER,
      MODEL_TYPE.SERVICE_ORDER
    ].includes(type)
  },
  lineHasInvoicing: (type) => {
    return [
      MODEL_TYPE.SALES_ORDER,
      MODEL_TYPE.SALES_RETURN_ORDER,
      MODEL_TYPE.PURCHASE_ORDER,
      MODEL_TYPE.PURCHASE_RETURN_ORDER,
      MODEL_TYPE.SERVICE_ORDER
    ].includes(type)
  },
  lineHasOrdering: (type) => {
    return [
      MODEL_TYPE.SALES_CONTRACT,
      MODEL_TYPE.PURCHASE_CONTRACT,
      MODEL_TYPE.SERVICE_CONTRACT
    ].includes(type)
  },
  lineAllColumns,
  lineReturnBlockColumns,
  lineAdditionalQuantityColumns,
  lineSetProduct: async (product, newData, data, company, documentHeader, vatPostingSetups, linesRef, returnMode, purchasePriceSource) => {
    /**
     * PRODUCT
     */
    newData.product_id = product.id
    newData.product = product

    newData.product_type = product.product_type
    newData.product_product_from_production = product.product_from_production
    newData.product_assembled_bom = product.assembled_bom
    newData.product_auto_assemble_bom = product.auto_assemble_bom
    newData.product_custom_bom = product.custom_bom
    newData.product_use_lot = product.use_lot
    newData.product_use_serial_numbers = product.use_serial_numbers
    newData.product_price_profit_calculation = product.price_profit_calculation
    newData.product_is_domestic = product.is_domestic

    if (
      (
        documentHeader.main_type === DOCUMENT_MAIN_TYPE.PURCHASE &&
        [DOCUMENT_LINE_TYPE.FIXED_ASSET, DOCUMENT_LINE_TYPE.SMALL_INVENTORY].includes(data.type)
      ) ||
      [MODEL_TYPE.RECLASSIFICATION_ORDER].includes(documentHeader.type)
    ) {
      const helper = MODEL_TYPE_MAPPER[MODEL_TYPE.PRODUCT]
      newData.description = helper.displayExpr(product, company.language_code)
    }

    if (DOCUMENT_HELPER.lineHasShipping(documentHeader.type) || DOCUMENT_HELPER.lineHasOrdering(documentHeader.type)) {
      newData.quantity_to_ship = 0
      newData.quantity_shipped = 0
      newData.outstanding_quantity_to_ship = 1
    }

    if (DOCUMENT_HELPER.lineHasInvoicing(documentHeader.type) || DOCUMENT_HELPER.lineHasOrdering(documentHeader.type)) {
      newData.quantity_to_invoice = 0
      newData.quantity_invoiced = 0
      newData.outstanding_quantity_to_invoice = 1
    }

    if (DOCUMENT_HELPER.lineHasOrdering(documentHeader.type)) {
      newData.quantity_to_order = 0
      newData.quantity_ordered = 0
      newData.outstanding_quantity_to_order = 1
    }

    if (returnMode) {
      newData.quantity_to_return = 0
      newData.quantity_returned = 0
    }

    newData.quantity_per_base = 1
    newData.quantity = 1

    newData.package_quantity_per_base = 1
    newData.package_quantity = 1

    newData.vat_percent = 0
    newData.discount_percentage = 0
    newData.list_price = 0
    newData.list_price_exc_vat = 0
    newData.price = 0
    newData.price_exc_vat = 0

    /**
     * UOM
     */
    const uom = documentHeader.main_type
      ? product[DOCUMENT_MAIN_TYPE_MAPPER.PRODUCT_UOM_RELATION[documentHeader.main_type]]
      : product.sales_uom

    const uomConversion = product.uoms.find(f => f.uom_id === uom.id)

    newData.uom_id = uom.id
    newData.uom = uom
    newData.quantity_per_base = uomConversion.quantity
    newData.quantity = 1

    const packageUomConversion = product.uoms.find(f => f.uom_id === product.package_uom_id)

    newData.package_uom_id = product.package_uom_id
    newData.package_uom = product.package_uom
    newData.package_quantity_per_base = packageUomConversion.quantity
    newData.package_quantity = newData.package_quantity_per_base / newData.quantity_per_base

    /**
     * VAT
     */
    const vatProductGroup = documentHeader.main_type
      ? product[DOCUMENT_MAIN_TYPE_MAPPER.PRODUCT_VAT_RELATION[documentHeader.main_type]]
      : product.sales_vat_product_group

    newData.vat_product_group_id = vatProductGroup.id
    newData.vat_product_group = vatProductGroup
    newData.vat_product_group_percent = vatProductGroup.percent

    DOCUMENT_LINE_HELPER_FUNCTION.lineSetVatPercent(newData, newData.vat_product_group_percent, vatProductGroup, documentHeader, vatPostingSetups)

    /**
     * PRICES
     */
    let listPrice = 0
    let price = 0

    if (documentHeader.main_type === DOCUMENT_MAIN_TYPE.PURCHASE) {
      if (purchasePriceSource === SETTING_PURCHASE_PRICE_SOURCE_VALUES.VENDOR_PRICE) {
        const productVendor = await axios.get(links.products.vendors.getData, {
          params: {
            product_id: product.id,
            vendor_id: documentHeader.account_id
          }
        })

        if (productVendor?.data?.id) {
          newData.vendor_id = documentHeader.account_id
          newData.vendor = documentHeader.account
          listPrice = productVendor?.data?.list_price
          price = productVendor?.data?.price
        }
      } else {
        // prices are zero
      }
    } else {
      listPrice = product.list_price
      price = product.price
    }

    newData.list_price = listPrice
    newData.list_price_exc_vat = newData.list_price / (1 + (newData.vat_product_group_percent / 100))

    if (newData.vat_percent) {
      newData.price = price
      newData.price_exc_vat = newData.price / (1 + (newData.vat_percent / 100))
    } else {
      newData.price = price / (1 + (newData.vat_product_group_percent / 100))
      newData.price_exc_vat = newData.price
    }

    DOCUMENT_HELPER.lineSetContractStartingPrice(newData, newData.price, documentHeader)

    const priceFields = ['list_price', 'list_price_exc_vat', 'price', 'price_exc_vat', 'contract_starting_price']
    priceFields.forEach(p => {
      if (newData[p]) {
        newData[p] = newData[p] / (documentHeader.currency_rate ?? 1)
      }
    })

    newData.unit_cost = 0
    newData.unit_price = product.list_price

    if (product.new_price_from_data) {
      newData.new_price_from_type = product.new_price_from_data.new_price_from_type
      newData.new_price_from_id = product.new_price_from_data.new_price_from_id
      newData.new_price_from_display = product.new_price_from_data.new_price_from_display
    }

    DOCUMENT_LINE_HELPER_FUNCTION.lineSetDiscountPercentage(newData, newData)

    DOCUMENT_LINE_HELPER_FUNCTION.lineSetTotals({ ...data, ...newData }, newData, documentHeader)

    if (linesRef) {
      setTimeout(() => {
        linesRef.current.instance.repaintRows([linesRef.current.instance.getRowIndexByKey(data.id)])
      })
    }
  },
  lineLoadProduct: async (newData, currentRowData) => {
    const product = currentRowData.product ?? { id: currentRowData.product_id }

    if (!product.uoms) {
      const productUoms = await axios.get(route(links.products.uom.getForProduct, { product: product.id }))
      product.uoms = productUoms.data

      newData.product = product
    }

    return product
  },
  lineSetVatPercent: (newData, percent, vatProductGroup, documentHeader, vatPostingSetups) => {
    const vps = vatPostingSetups.find(
      f =>
        f.vat_product_posting_group_id === vatProductGroup.id &&
        f.vat_business_posting_group_id === documentHeader.vat_business_group.id
    )

    newData.vat_percent = vps ? vps.percent : percent
    newData.vat_calculation_type = vps ? vps.vat_calculation_type : enumConstants.vat_calculation_types.NORMAL_VAT
  },
  lineSetDiscountPrices: (data, newData, documentHeader) => {
    const listPriceExcVat = data.list_price_exc_vat ?? 0
    const listPrice = data.list_price ?? 0
    const discountPercentage = data.discount_percentage ?? 0
    const vatPercent = data.vat_percent ?? 0

    const discountExcVat = listPriceExcVat * discountPercentage / 100
    const discount = vatPercent
      ? listPrice * discountPercentage / 100
      : discountExcVat

    newData.price_exc_vat = parseFloat((listPriceExcVat - discountExcVat).toFixed(6))
    newData.price = parseFloat((listPrice - discount).toFixed(6))

    DOCUMENT_HELPER.lineSetContractStartingPrice(newData, newData.price, documentHeader)

    DOCUMENT_LINE_HELPER_FUNCTION.lineClearNewPriceFrom(newData)
  },
  lineSetDiscountPercentage: (data, newData) => {
    newData.discount_percentage = data.list_price_exc_vat
      ? (1 - parseFloat(data.price_exc_vat) / parseFloat(data.list_price_exc_vat)) * 100
      : 0

    newData.discount_percentage = parseFloat(newData.discount_percentage.toFixed(6))
  },
  lineSetDiscountPercentage2: (data, newData) => {
    newData.discount_percentage = data.list_price
      ? (1 - parseFloat(data.price) / parseFloat(data.list_price)) * 100
      : 0

    newData.discount_percentage = parseFloat(newData.discount_percentage.toFixed(6))
  },
  lineSetTotals: (data, newData, documentHeader, skips = [], linesRef, quantityKey) => {
    const quantity = data[quantityKey ?? 'quantity'] ?? 0
    const priceExcVat = data.price_exc_vat ?? 0
    const price = data.price ?? 0
    const listPriceExcVat = data.list_price_exc_vat ?? 0
    const listPrice = data.list_price ?? 0
    const discountPercentage = data.discount_percentage ?? 0
    const vatPercent = data.vat_percent ?? 0

    skips.forEach(s => {
      newData[s] = data[s]
    })

    newData.base_amount = quantity * listPriceExcVat
    newData.base_total = vatPercent
      ? quantity * listPrice
      : newData.base_amount

    if (!skips.includes('unit_discount_exc_vat')) {
      newData.unit_discount_exc_vat = listPriceExcVat * discountPercentage / 100
    }
    if (!skips.includes('unit_discount')) {
      newData.unit_discount = vatPercent
        ? listPrice * discountPercentage / 100
        : newData.unit_discount_exc_vat
    }

    if (!skips.includes('discount_amount')) {
      newData.discount_amount = quantity * newData.unit_discount_exc_vat
    }
    if (!skips.includes('discount_total')) {
      newData.discount_total = quantity * newData.unit_discount
    }

    if (!skips.includes('amount')) {
      newData.amount = quantity * priceExcVat
    }
    if (!skips.includes('total')) {
      newData.total_exc_rounding = quantity * price
      newData.total = parseFloat(newData.total_exc_rounding).toFixed(data.rounding_precision)
    }
    newData.vat_amount = newData.total_exc_rounding - newData.amount

    if (
      documentHeader?.main_type !== DOCUMENT_MAIN_TYPE.PURCHASE &&
      data.type === DOCUMENT_LINE_TYPE.FIXED_ASSET &&
      data.helper_json &&
      priceExcVat < data.helper_json.current_value
    ) {
      toast.info(t('messages.fixed_asset_sales_price_lower_than_current_value', { currentValue: data.helper_json.current_value }))
    }

    if (linesRef) {
      setTimeout(() => {
        linesRef.current.instance.repaintRows([linesRef.current.instance.getRowIndexByKey(data.id)])
      })
    }
  },
  lineReset: (newData, ignore = []) => {
    const nullColumns = [
      'type',
      'model_id',
      'model_type',
      'model_type_enum',
      'description',
      'description_2',

      'product_id',
      'product_type',

      'package_uom_id',
      'package_uom',
      'package_quantity',
      'package_quantity_per_base',

      'uom_id',
      'uom',
      'quantity',
      'quantity_per_base',

      'list_price',
      'list_price_exc_vat',

      'price',
      'price_exc_vat',

      'discount_percentage',
      'unit_discount_exc_vat',
      'unit_discount',
      'discount_amount',
      'discount_total',

      'vat_product_group_id',
      'vat_calculation_type',

      'base_amount',
      'amount',
      'vat_percent',
      'vat_amount',
      'base_total',
      'total',

      'note',

      'vendor_id'
    ]

    for (const column of nullColumns) {
      if (!ignore.includes(column)) {
        newData[column] = null
      }
    }

    const zeroColumns = [
      'quantity_returned',

      'quantity_released',

      'quantity_to_order',
      'quantity_ordered',
      'outstanding_quantity_to_order',

      'quantity_to_ship',
      'quantity_shipped',
      'outstanding_quantity_to_ship',

      'quantity_to_invoice',
      'quantity_invoiced',
      'outstanding_quantity_to_invoice'
    ]

    for (const column of zeroColumns) {
      if (!ignore.includes(column)) {
        newData[column] = 0
      }
    }
  },
  lineQuantityChanged: async (documentHeader, data, newData) => {
    if (
      [DOCUMENT_MAIN_TYPE.SALES, DOCUMENT_MAIN_TYPE.SALES_RETAIL, DOCUMENT_MAIN_TYPE.SERVICE].includes(documentHeader.main_type) &&
      data.type === DOCUMENT_LINE_TYPE.PRODUCT
    ) {
      const params = {
        load_relations: false,
        account_id: documentHeader.account_id,
        location_id: documentHeader.location_id,
        document_date: documentHeader.date,
        document_type: documentHeader.type,
        document_line_type: data.type,
        quantity: data.quantity
      }

      const productHelper = MODEL_TYPE_MAPPER[MODEL_TYPE.PRODUCT]
      const product = await productHelper.dataSource({ params }).byKey(data.product_id)

      newData.list_price = product.list_price
      newData.list_price_exc_vat = newData.list_price / (1 + (data.vat_product_group_percent / 100))

      if (data.vat_percent) {
        newData.price = product.price
        newData.price_exc_vat = newData.price / (1 + (data.vat_percent / 100))
      } else {
        newData.price = product.price / (1 + (data.vat_product_group_percent / 100))
        newData.price_exc_vat = newData.price
      }

      DOCUMENT_HELPER.lineSetContractStartingPrice(newData, newData.price, documentHeader)

      if (product.new_price_from_data) {
        newData.new_price_from_type = product.new_price_from_data.new_price_from_type
        newData.new_price_from_id = product.new_price_from_data.new_price_from_id
        newData.new_price_from_display = product.new_price_from_data.new_price_from_display
      }
    }

    return new Promise(function (resolve) {
      resolve()
    })
  },
  lineClearNewPriceFrom: async (newData) => {
    newData.new_price_from_type = null
    newData.new_price_from_id = null
    newData.new_price_from_display = null
  },
  lineSetContractStartingPrice: (newData, price, documentHeader) => {
    if (
      DOCUMENT_HELPER.lineHasOrdering(documentHeader?.type) &&
      ![MODEL_PHASE_TYPE.APPROVED, MODEL_PHASE_TYPE.PARTIALLY_PROCESSED, MODEL_PHASE_TYPE.FULLY_PROCESSED].includes(documentHeader.status_phase_type)
    ) {
      newData.contract_starting_price = price
    }
  },
  lineHandleBarcodeScan: (barcode, config) => {
    axios.get(
      links.products.resourceByBarcode, {
        params: {
          barcode
        }
      })
      .then(async response => {
        const product = response.data
        if (product) {
          let findLineResponse = null

          if (config.sumQuantityWithBarcodeScan) {
            findLineResponse = await axios.get(
              config.findLineRoute, {
                params: {
                  product_id: product.id
                }
              })
          }

          if (findLineResponse && findLineResponse?.data?.id) {
            const line = findLineResponse.data
            const lineData = await config.updateLineDataCallback(line)

            await axios.put(config.updateLineRoute.replace('#line_id#', line.id), lineData
            )
              .then(() => {
                toast.success(t('messages.updated_successfully'))
                config.successCallback && config.successCallback()
              })
          } else {
            const lineData = await config.addLineDataCallback(product)

            axios.post(config.addLineRoute, lineData)
              .then(() => {
                toast.success(t('messages.created_successfully'))
                config.successCallback && config.successCallback()
              })
          }
        } else {
          toast.error(t('messages.product_with_barcode_not_found', { productBarcode: barcode }))
        }
      })
      .finally(() => {
        config.finallyCallback && config.finallyCallback()
      })
  }
}

const DOCUMENT_RETURN_FUNCTION = {
  lineHasReturning: (documentHeader) => {
    return documentHeader &&
      headerInClosedPhases(documentHeader) &&
      [
        MODEL_TYPE.SALES_RETAIL_TRANSACTION,
        MODEL_TYPE.SALES_INVOICE,
        MODEL_TYPE.SALES_ORDER,
        MODEL_TYPE.SALES_SHIPMENT,
        MODEL_TYPE.PURCHASE_INVOICE,
        MODEL_TYPE.PURCHASE_ORDER,
        MODEL_TYPE.PURCHASE_RECEIPT,
        MODEL_TYPE.PURCHASE_RETAIL_TRANSACTION
      ].includes(documentHeader.type)
  },
  lineHasRelationReturning: (documentHeader) => {
    return documentHeader &&
      headerInClosedPhases(documentHeader) &&
      [
        MODEL_TYPE.SALES_INVOICE,
        MODEL_TYPE.SALES_SHIPMENT,
        MODEL_TYPE.PURCHASE_INVOICE,
        MODEL_TYPE.PURCHASE_RECEIPT
      ].includes(documentHeader.type)
  },
  getReturnDocument: (type) => {
    const map = {
      [MODEL_TYPE.SALES_RETAIL_TRANSACTION]: MODEL_TYPE.SALES_RETAIL_REFUND_TRANSACTION,
      [MODEL_TYPE.SALES_INVOICE]: MODEL_TYPE.SALES_CREDIT_MEMO,
      [MODEL_TYPE.SALES_ORDER]: MODEL_TYPE.SALES_RETURN_ORDER,
      [MODEL_TYPE.SALES_SHIPMENT]: MODEL_TYPE.SALES_RETURN_SHIPMENT,
      [MODEL_TYPE.PURCHASE_INVOICE]: MODEL_TYPE.PURCHASE_CREDIT_MEMO,
      [MODEL_TYPE.PURCHASE_ORDER]: MODEL_TYPE.PURCHASE_RETURN_ORDER,
      [MODEL_TYPE.PURCHASE_RECEIPT]: MODEL_TYPE.PURCHASE_RETURN_RECEIPT,
      [MODEL_TYPE.PURCHASE_RETAIL_TRANSACTION]: MODEL_TYPE.PURCHASE_RETAIL_REFUND_TRANSACTION
    }

    return map[type]
  },
  getRelationReturnDocument: (type) => {
    const map = {
      [MODEL_TYPE.SALES_INVOICE]: MODEL_TYPE.SALES_RETURN_SHIPMENT,
      [MODEL_TYPE.SALES_SHIPMENT]: MODEL_TYPE.SALES_CREDIT_MEMO,
      [MODEL_TYPE.PURCHASE_INVOICE]: MODEL_TYPE.PURCHASE_RETURN_RECEIPT,
      [MODEL_TYPE.PURCHASE_RECEIPT]: MODEL_TYPE.PURCHASE_CREDIT_MEMO
    }

    return map[type]
  },
  getReturnDocumentCode: (type) => {
    return MODEL_TYPE_KEY_BY_VALUE(DOCUMENT_HELPER.getReturnDocument(type)).toLowerCase()
  },
  getRelatedReturnDocumentCode: (type) => {
    return MODEL_TYPE_KEY_BY_VALUE(DOCUMENT_HELPER.getRelationReturnDocument(type)).toLowerCase()
  }
}

const DOCUMENT_HEADER_HELPER_FUNCTION = {
  headerHasShipping: (type) => {
    return [
      MODEL_TYPE.PURCHASE_CONTRACT,
      MODEL_TYPE.PURCHASE_ORDER,
      MODEL_TYPE.PURCHASE_RETURN_ORDER,
      MODEL_TYPE.PURCHASE_INVOICE,
      MODEL_TYPE.PURCHASE_CREDIT_MEMO,
      MODEL_TYPE.PURCHASE_RECEIPT,
      MODEL_TYPE.PURCHASE_RETURN_RECEIPT,
      MODEL_TYPE.PURCHASE_RETAIL_TRANSACTION,
      MODEL_TYPE.PURCHASE_RETAIL_REFUND_TRANSACTION,
      MODEL_TYPE.SALES_CONTRACT,
      MODEL_TYPE.SALES_ORDER,
      MODEL_TYPE.SALES_RETURN_ORDER,
      MODEL_TYPE.SALES_INVOICE,
      MODEL_TYPE.SALES_CREDIT_MEMO,
      MODEL_TYPE.SALES_SHIPMENT,
      MODEL_TYPE.SALES_RETURN_SHIPMENT,
      MODEL_TYPE.SALES_RETAIL_TRANSACTION,
      MODEL_TYPE.SALES_RETAIL_REFUND_TRANSACTION,
      MODEL_TYPE.SERVICE_ORDER,
      MODEL_TYPE.SERVICE_CONTRACT,
      MODEL_TYPE.SERVICE_SHIPMENT,
      MODEL_TYPE.SERVICE_INVOICE
    ].includes(type)
  },
  headerReloadLines: (documentHeader, data) => {
    let reload = false

    if (data.vat_business_group_id !== documentHeader.vat_business_group_id) {
      reload = true
    }

    if (data.location_id !== documentHeader.location_id) {
      reload = true
    }

    if (data.responsibility_center_id !== documentHeader.responsibility_center_id) {
      reload = true
    }

    if (data.shipment_location_id !== documentHeader.shipment_location_id) {
      reload = true
    }

    if (data.shipment_responsibility_center_id !== documentHeader.shipment_responsibility_center_id) {
      reload = true
    }

    if (data.account_id !== documentHeader.account_id) {
      reload = true
    }

    if (data.date !== documentHeader.date) {
      reload = true
    }

    return reload
  },
  headerReloadTotals: (documentHeader, data) => {
    let reload = false

    if (data.vat_business_group_id !== documentHeader.vat_business_group_id) {
      reload = true
    }

    if (data.location_id !== documentHeader.location_id) {
      reload = true
    }

    if (data.account_id !== documentHeader.account_id) {
      reload = true
    }

    if (data.date !== documentHeader.date) {
      reload = true
    }

    return reload
  },
  headerHasService: (type) => {
    return [
      MODEL_TYPE.SERVICE_ORDER,
      MODEL_TYPE.SERVICE_CONTRACT,
      MODEL_TYPE.SERVICE_SHIPMENT,
      MODEL_TYPE.SERVICE_INVOICE
    ].includes(type)
  },
  headerIsMaterial: (type) => {
    return [
      MODEL_TYPE.SALES_SHIPMENT,
      MODEL_TYPE.SALES_RETURN_SHIPMENT,
      MODEL_TYPE.PURCHASE_RECEIPT,
      MODEL_TYPE.PURCHASE_RETURN_RECEIPT,
      MODEL_TYPE.SERVICE_SHIPMENT
    ].includes(type)
  },
  headerIsInvoice: (type) => {
    return [
      MODEL_TYPE.PURCHASE_INVOICE,
      MODEL_TYPE.SALES_INVOICE,
      MODEL_TYPE.SERVICE_INVOICE
    ].includes(type)
  },
  headerIsShipment: (type) => {
    return [
      MODEL_TYPE.PURCHASE_RECEIPT,
      MODEL_TYPE.SALES_SHIPMENT,
      MODEL_TYPE.SERVICE_SHIPMENT
    ].includes(type)
  },
  headerIsBaseOrder: (type) => {
    return [
      MODEL_TYPE.PURCHASE_ORDER,
      MODEL_TYPE.SALES_ORDER,
      MODEL_TYPE.SERVICE_ORDER
    ].includes(type)
  },
  headerIsSalesRetail: (type) => {
    return [
      MODEL_TYPE.SALES_RETAIL_TRANSACTION,
      MODEL_TYPE.SALES_RETAIL_REFUND_TRANSACTION
    ].includes(type)
  },
  headerIsAdvanceInvoice: (type) => {
    return [
      MODEL_TYPE.PURCHASE_ADVANCE_INVOICE,
      MODEL_TYPE.SALES_ADVANCE_INVOICE
    ].includes(type)
  },
  headerIsReturn: (type) => {
    return [
      MODEL_TYPE.SALES_CREDIT_MEMO,
      MODEL_TYPE.SALES_RETURN_SHIPMENT,
      MODEL_TYPE.SALES_RETURN_ORDER,
      MODEL_TYPE.PURCHASE_CREDIT_MEMO,
      MODEL_TYPE.PURCHASE_RETURN_RECEIPT,
      MODEL_TYPE.PURCHASE_RETURN_ORDER,
      MODEL_TYPE.PURCHASE_RETAIL_REFUND_TRANSACTION,
      MODEL_TYPE.SALES_RETAIL_REFUND_TRANSACTION
    ].includes(type)
  },
  headerIsPayable: (type) => {
    return [
      MODEL_TYPE.SALES_INVOICE,
      MODEL_TYPE.SALES_ADVANCE_INVOICE,
      MODEL_TYPE.SALES_CREDIT_MEMO,
      MODEL_TYPE.SALES_FINANCE_CHARGE,
      MODEL_TYPE.PURCHASE_INVOICE,
      MODEL_TYPE.PURCHASE_ADVANCE_INVOICE,
      MODEL_TYPE.PURCHASE_CREDIT_MEMO,
      MODEL_TYPE.PURCHASE_FINANCE_CHARGE,
      MODEL_TYPE.SERVICE_INVOICE
    ].includes(type)
  },
  headerIsRetailPayable: (type) => {
    return [
      MODEL_TYPE.SALES_RETAIL_TRANSACTION,
      MODEL_TYPE.SALES_RETAIL_REFUND_TRANSACTION,
      MODEL_TYPE.PURCHASE_RETAIL_TRANSACTION,
      MODEL_TYPE.PURCHASE_RETAIL_REFUND_TRANSACTION
    ].includes(type)
  },
  headerCanForwardReconciliation: (type) => {
    return [
      MODEL_TYPE.SALES_ADVANCE_INVOICE,
      MODEL_TYPE.SALES_ORDER,
      MODEL_TYPE.SALES_RETURN_ORDER,
      MODEL_TYPE.PURCHASE_ADVANCE_INVOICE,
      MODEL_TYPE.PURCHASE_ORDER,
      MODEL_TYPE.PURCHASE_RETURN_ORDER,
      MODEL_TYPE.SERVICE_ORDER
    ].includes(type)
  },
  getHeaderShipmentGetInvoice: (type) => {
    const map = {
      [MODEL_TYPE.PURCHASE_RECEIPT]: MODEL_TYPE.PURCHASE_INVOICE,
      [MODEL_TYPE.PURCHASE_RETURN_RECEIPT]: MODEL_TYPE.PURCHASE_CREDIT_MEMO,
      [MODEL_TYPE.SALES_SHIPMENT]: MODEL_TYPE.SALES_INVOICE,
      [MODEL_TYPE.SALES_RETURN_SHIPMENT]: MODEL_TYPE.SALES_CREDIT_MEMO,
      [MODEL_TYPE.SERVICE_SHIPMENT]: MODEL_TYPE.SERVICE_INVOICE
    }

    return map[type]
  },
  getMaterialDocumentCode: (type) => {
    return MODEL_TYPE_KEY_BY_VALUE(DOCUMENT_HELPER.getHeaderShipmentGetInvoice(type)).toLowerCase()
  },
  headerInClosedPhases,
  headerCalculateTotals: (lines, quantityKey = 'quantity') => {
    const sums = {
      base_amount: 0,
      base_total: 0,
      discount_amount: 0,
      discount_total: 0,
      total: 0,
      amount: 0,
      vat_amount: 0
    }

    lines.forEach(ci => {
      const quantity = ci[quantityKey]

      sums.base_amount += quantity * ci.list_price_exc_vat
      sums.base_total += quantity * ci.list_price
      sums.discount_amount += quantity * ci.unit_discount_exc_vat
      sums.discount_total += quantity * ci.unit_discount
      sums.total += quantity * ci.price
      sums.amount += quantity * ci.price_exc_vat
      sums.vat_amount += quantity * (ci.price - ci.price_exc_vat)
    })

    const totals = {}

    totals.base_amount = sums.base_amount ?? 0
    totals.base_total = sums.base_total ?? 0

    totals.discount_amount = sums.discount_amount ?? 0
    totals.discount_total = sums.discount_total ?? 0
    totals.discount_percentage = totals.base_amount > 0
      ? totals.discount_amount / totals.base_amount * 100
      : 0

    totals.total_exc_rounding = sums.total ?? 0
    totals.total = parseFloat(totals.total_exc_rounding.toFixed(totals.rounding_precision))

    totals.amount = sums.amount ?? 0
    totals.vat_amount = sums.vat_amount ?? 0

    return totals
  },
  getDocumentTypeCode: (type) => {
    return MODEL_TYPE_KEY_BY_VALUE(type).toLowerCase()
  },
  getDocumentTypeLink: (type) => {
    return DOCUMENT_HELPER.getDocumentTypeCode(type).replace(/_/g, '-')
  },
  actionResponsesNewDocumentHeaders: (actionResponses, modalRef) => {
    const newDocumentHeaders = []

    actionResponses && actionResponses.forEach(ar => {
      if (ar.newDocumentHeader) {
        newDocumentHeaders.push(ar.newDocumentHeader)
      }
    })

    if (newDocumentHeaders.length) {
      modalRef.current.open()
      modalRef.current.setInnerData({
        title: t('messages.processed_successfully'),
        body: <NewDocumentHeaders newDocumentHeaders={newDocumentHeaders} />,
        modalProps: {
          width: 450,
          hideConfirmButton: true
        }
      })
    }
  }
}

export const DOCUMENT_HELPER = {
  ...DOCUMENT_LINE_HELPER_FUNCTION,
  ...DOCUMENT_RETURN_FUNCTION,
  ...DOCUMENT_HEADER_HELPER_FUNCTION
}
